/**
 * AuthContext for apps/web.
 *
 * Responsibilities:
 *   - Hydrate the user from a stored refresh/access token on app boot
 *   - Expose `login`, `logout`, `signUp`, `refreshUser` to the UI
 *   - Wire `api.onUnauthorized` → navigate('/login') so the silent-refresh
 *     failure path bounces users to sign in (per SC-20260417-001 decision:
 *     silent refresh first, kick to login only on refresh failure)
 *
 * This intentionally does NOT model MFA (consumer web has no MFA in v1 —
 * OS/staff does). When MFA ships for consumers, fold it in here mirroring
 * apps/pelipost-os/src/context/AuthContext.tsx.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthResponse } from '@pelipost/consumer-api-client';
import {
  api,
  clearTokens,
  getAccessToken,
  setOnUnauthorized,
  storeTokens,
} from '../lib/api/client';
import { setSentryUser } from '../lib/sentry';
import { chatService } from '../lib/chatService';

type AuthUser = AuthResponse['user'];

const SELECTED_CONTACT_STORAGE_KEY = 'pelipost.web.selectedContactId';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** True only during the initial boot-time hydration from storage. */
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  signUp: (args: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
  }) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Trigger Twilio to send a 6-digit code to the signed-in user's phone. */
  requestPhoneVerification: () => Promise<void>;
  /** Submit the 6-digit code the user typed. Re-hydrates the user on success. */
  verifyPhone: (code: string) => Promise<void>;
  /** Kick off a password-reset email. Never reveals whether the email exists. */
  forgotPassword: (email: string) => Promise<void>;
  /**
   * Globally-selected recipient (contact). Used by the TopBar selector and
   * product flows that need a "current" recipient context. Persisted to
   * localStorage so the user's selection survives refreshes. PEL-822.
   */
  selectedContactId: string | null;
  setSelectedContactId: (id: string | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedContactId, setSelectedContactIdState] = useState<string | null>(
    () => {
      try {
        return localStorage.getItem(SELECTED_CONTACT_STORAGE_KEY);
      } catch {
        return null;
      }
    },
  );
  const navigate = useNavigate();

  const setSelectedContactId = useCallback((id: string | null) => {
    setSelectedContactIdState(id);
    try {
      if (id) {
        localStorage.setItem(SELECTED_CONTACT_STORAGE_KEY, id);
      } else {
        localStorage.removeItem(SELECTED_CONTACT_STORAGE_KEY);
      }
    } catch {
      // localStorage may be unavailable (private-browsing Safari). Non-fatal
      // — selection just won't persist across refreshes.
    }
  }, []);

  // Avoid racing multiple redirects if several in-flight requests all 401
  // simultaneously during session expiry.
  const redirectingRef = useRef(false);

  // ── onUnauthorized wiring ─────────────────────────────────────
  //
  // The api client is constructed at module load (before React is mounted),
  // so we register the redirect handler here after Router is available.
  useEffect(() => {
    setOnUnauthorized(() => {
      if (redirectingRef.current) return;
      redirectingRef.current = true;
      clearTokens();
      setUser(null);
      navigate('/login', { replace: true });
      // Reset the flag on next tick so legitimate future 401s still redirect.
      setTimeout(() => {
        redirectingRef.current = false;
      }, 500);
    });
    return () => setOnUnauthorized(null);
  }, [navigate]);

  // ── chatService lifecycle (PEL-836) ───────────────────────────
  //
  // Connect the Socket.IO client whenever we have a user + access token, and
  // disconnect on logout. Running this here (rather than in an InboxPage
  // useEffect) means the badge unread-count stays live across the whole app.
  useEffect(() => {
    if (!user) return;
    const token = getAccessToken();
    if (!token) return;
    chatService.connect(import.meta.env.VITE_API_URL as string, token);
    return () => {
      chatService.disconnect();
    };
  }, [user]);

  // ── Boot-time hydration ───────────────────────────────────────
  const refreshUser = useCallback(async () => {
    try {
      const profile = await api.auth.getProfile();
      setUser(profile);
      setSentryUser(profile.id);
    } catch {
      // `api.onUnauthorized` will have cleared tokens + redirected if this
      // was a 401-after-refresh-failure. For any other error we stay silent
      // and leave user=null; UI will show the login prompt.
      setUser(null);
      setSentryUser(null);
    }
  }, []);

  useEffect(() => {
    const hasToken = getAccessToken() !== null;
    if (!hasToken) {
      setIsLoading(false);
      return;
    }
    refreshUser().finally(() => setIsLoading(false));
  }, [refreshUser]);

  // ── Actions ───────────────────────────────────────────────────
  const login = useCallback(
    async (email: string, password: string) => {
      const response = await api.auth.login({ email, password });
      storeTokens(response.accessToken, response.refreshToken);
      setUser(response.user);
      setSentryUser(response.user.id);
      return response.user;
    },
    [],
  );

  const signUp = useCallback(
    async (args: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      phone?: string;
    }) => {
      const response = await api.auth.signUp(args);
      storeTokens(response.accessToken, response.refreshToken);
      setUser(response.user);
      setSentryUser(response.user.id);
      return response.user;
    },
    [],
  );

  const requestPhoneVerification = useCallback(async () => {
    await api.auth.requestPhoneVerification();
  }, []);

  const verifyPhone = useCallback(
    async (code: string) => {
      await api.auth.verifyPhone({ code });
      // Re-pull the profile so any server-side `phoneVerified` flag etc is current.
      await refreshUser();
    },
    [refreshUser],
  );

  const forgotPassword = useCallback(async (email: string) => {
    await api.auth.forgotPassword(email);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // Best effort — if the server-side logout fails we still clear local
      // state so the user isn't stuck.
    }
    clearTokens();
    setUser(null);
    setSentryUser(null);
    setSelectedContactId(null);
    navigate('/login', { replace: true });
  }, [navigate, setSelectedContactId]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      login,
      signUp,
      logout,
      refreshUser,
      requestPhoneVerification,
      verifyPhone,
      forgotPassword,
      selectedContactId,
      setSelectedContactId,
    }),
    [
      user,
      isLoading,
      login,
      signUp,
      logout,
      refreshUser,
      requestPhoneVerification,
      verifyPhone,
      forgotPassword,
      selectedContactId,
      setSelectedContactId,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}
